import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useCallback, useMemo, useRef, useState, } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'Shared/hooks';
import { MULTISELECT_FILTERING_DELAY } from 'Common/consts';
import { getResetButtonStatus } from 'Common/utils';
import { setFiltersVLR } from 'PhenotypeView/store/slice';
import { getConsequenceTypeFilterHierarchy, getFiltersData, getIsFiltering } from 'PhenotypeView/store/selectors/levelResults/vlr/vlr';
import Filters from './Filters';
const FiltersContainer = ({ traitsType, exportedHeaders, getBinaryPlotDataPointShape, getContinuousPlotDataPointShape, filtersSectionRef, hasStickyShadow, hasAncestryData, }) => {
    const { ancestries, filters, genes, variants, collapsingModels, consequenceTypes: allConsequenceTypes, } = useSelector(getFiltersData);
    const isFiltering = useSelector(getIsFiltering);
    const ancestriesRef = useRef(ancestries);
    const collapsingModelsRef = useRef(collapsingModels);
    const consequenceTypesRef = useRef(allConsequenceTypes);
    const hiddenModelsRef = useRef([]);
    const [filteredAncestries, setFilteredAncestries] = useState(filters.ancestries);
    const [models, setModels] = useState(filters.collapsingModels);
    const [consequenceTypes, setConsequenceTypes] = useState(filters.consequenceTypes);
    const [isMostSignificantModel, setIsMostSignificantModel,] = useState(filters.isMostSignificantModel);
    const [isDirectionOfEffectFilterActive, setIsDirectionOfEffectFilterActive] = useState(filters.isDirectionOfEffectFilterActive);
    const dispatch = useDispatch();
    const handleAncestriesChangeDebounced = useDebounce((updatedAncestries) => {
        dispatch(setFiltersVLR({ ancestries: updatedAncestries }));
    }, MULTISELECT_FILTERING_DELAY);
    const handleModelsChangeDebounced = useDebounce((updatedModels) => {
        dispatch(setFiltersVLR({ collapsingModels: updatedModels }));
    }, MULTISELECT_FILTERING_DELAY);
    const handleModelsChange = useCallback((updatedModels) => {
        setModels(updatedModels);
        handleModelsChangeDebounced(updatedModels);
    }, [handleModelsChangeDebounced]);
    const handleAncestriesChange = useCallback((updatedAncestries) => {
        setFilteredAncestries(updatedAncestries);
        handleAncestriesChangeDebounced(updatedAncestries);
    }, [handleAncestriesChangeDebounced]);
    const handleConsequenceTypesDebounced = useDebounce((updatedConsequenceTypes) => {
        dispatch(setFiltersVLR({ consequenceTypes: updatedConsequenceTypes }));
    }, MULTISELECT_FILTERING_DELAY);
    const handleConsequenceTypeChange = useCallback((updatedConsequenceTypes) => {
        const mappedConsequenceTypes = updatedConsequenceTypes.map((consequenceType) => consequenceType.label);
        setConsequenceTypes(mappedConsequenceTypes);
        handleConsequenceTypesDebounced(mappedConsequenceTypes);
    }, [handleConsequenceTypesDebounced]);
    const handleMaxPValueChange = useCallback((updatedValue) => {
        dispatch(setFiltersVLR({ maxPValue: updatedValue }));
    }, [dispatch]);
    const handleGeneChange = useCallback((updatedValue) => {
        if (updatedValue !== filters.gene) {
            dispatch(setFiltersVLR({ gene: updatedValue }));
        }
    }, [filters.gene, dispatch]);
    const handleVariantChange = useCallback((updatedValue) => {
        if (updatedValue !== filters.variant) {
            dispatch(setFiltersVLR({ variant: updatedValue }));
        }
    }, [filters.variant, dispatch]);
    const handleIsMostSignificantModelChange = useCallback((checked) => {
        setIsMostSignificantModel(checked);
        dispatch(setFiltersVLR({ isMostSignificantModel: checked }));
    }, [dispatch, setIsMostSignificantModel]);
    const handleIsDirectionOfEffectChange = useCallback((checked) => {
        setIsDirectionOfEffectFilterActive(checked);
        dispatch(setFiltersVLR({ isDirectionOfEffectFilterActive: checked }));
    }, [dispatch, setIsDirectionOfEffectFilterActive]);
    const handleReset = useCallback(() => {
        setFilteredAncestries(ancestriesRef.current);
        setModels(collapsingModelsRef.current);
        setConsequenceTypes(consequenceTypesRef.current);
        setIsMostSignificantModel(false);
        setIsDirectionOfEffectFilterActive(false);
        hiddenModelsRef.current = [];
        dispatch(setFiltersVLR({
            ancestries: ancestriesRef.current,
            collapsingModels: collapsingModelsRef.current,
            consequenceTypes: consequenceTypesRef.current,
            maxPValue: null,
            gene: null,
            variant: null,
            isMostSignificantModel: false,
            isDirectionOfEffectFilterActive: false,
        }));
    }, [dispatch]);
    const isFiltersNotChanged = useMemo(() => getResetButtonStatus(filters, collapsingModelsRef.current, null, consequenceTypesRef.current, ancestriesRef.current), [filters]);
    const consequenceTypeHierarchy = useSelector(getConsequenceTypeFilterHierarchy);
    return (_jsx(Filters, { sectionRef: filtersSectionRef, traitsType: traitsType, ancestries: ancestries, collapsingModels: collapsingModels, consequenceTypes: consequenceTypeHierarchy, genes: genes, variants: variants, filters: Object.assign(Object.assign({}, filters), { ancestries: filteredAncestries, collapsingModels: models, consequenceTypes,
            isMostSignificantModel,
            isDirectionOfEffectFilterActive }), onAncestriesChange: handleAncestriesChange, onModelsChange: handleModelsChange, onConsequenceTypesChange: handleConsequenceTypeChange, onMaxPValueChange: handleMaxPValueChange, onIsMostSignificantModelChange: handleIsMostSignificantModelChange, onIsDirectionOfEffect: handleIsDirectionOfEffectChange, onGeneChange: handleGeneChange, onVariantChange: handleVariantChange, exportedHeaders: exportedHeaders, getBinaryPlotDataPointShape: getBinaryPlotDataPointShape, getContinuousPlotDataPointShape: getContinuousPlotDataPointShape, disabled: isFiltering, hasStickyShadow: hasStickyShadow, handleReset: handleReset, isFiltersNotChanged: isFiltersNotChanged, hasAncestryData: hasAncestryData }, void 0));
};
export default memo(FiltersContainer);
